import type { FormField } from '@lucid/lego';
import {
  carMakerOptions,
  emailConsentOptions,
  financeTypeOptions,
  interestedModelOptions,
  phoneConsentOptions,
  preferredContactMethodOptions,
  purchaseTimingOptions,
  purchaseTypeOptions,
  statusOptions,
  testDriveTimeOptions,
  titleOptions,
} from '../constants';

type CreateLeadFormFieldsOptions = {
  onCancel?: () => void;
  location?: string;
  organizationName?: string;
  onPostalCodeChange?: (value: unknown) => void;
};

export const createLeadFormFields = (options?: CreateLeadFormFieldsOptions): FormField[] => [
  // Personal Information Section
  {
    id: 'informationGroup',
    fieldType: 'group',
    title: '',
    width: 'full',
    children: [
      {
        id: 'leadFirstName',
        fieldType: 'text',
        name: 'prospectFirstName',
        apiKey: 'prospectFirstName',
        label: 'First Name',
        placeholder: 'Enter First Name',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: 'First name is required',
        },
      },
      {
        id: 'leadMiddleName',
        fieldType: 'text',
        name: 'middleName',
        apiKey: 'prospectMiddleName',
        label: 'Middle Name',
        width: 'quarter',
        placeholder: 'Enter Middle Name',
      },
      {
        id: 'leadLastName',
        fieldType: 'text',
        name: 'prospectLastName',
        apiKey: 'prospectLastName',
        label: 'Last Name',
        placeholder: 'Enter Last Name',
        required: true,
        width: 'quarter',
        validationMessages: {
          required: 'Last name is required',
        },
      },
      {
        id: 'title',
        fieldType: 'dropdown',
        name: 'title',
        apiKey: 'prospectTitle',
        label: 'Title',
        placeholder: 'Select',
        required: false,
        width: 'quarter',
        options: titleOptions,
      },
      {
        id: 'leadEmail',
        fieldType: 'email',
        name: 'email',
        apiKey: 'prospectEmail',
        label: 'Email',
        placeholder: 'customer@example.com',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'Email is required',
          pattern: 'Please enter a valid email address',
        },
      },
      {
        id: 'leadPhone',
        fieldType: 'phone',
        name: 'prospectPhone',
        apiKey: 'prospectPhone',
        label: 'Phone',
        required: false,
        width: 'half',
        selectedOption: 'US',
        validationMessages: {
          pattern: 'Please enter a valid phone number',
        },
      },
      {
        id: 'zipCode',
        fieldType: 'text',
        name: 'zipCode',
        apiKey: 'zipCode',
        label: 'Postal Code',
        placeholder: 'Enter Postal Code',
        required: true,
        width: 'full',
        onChange: options?.onPostalCodeChange,
        validationMessages: {
          required: 'Postal code is required',
        },
      },
      {
        id: 'city',
        fieldType: 'text',
        name: 'city',
        apiKey: 'city',
        label: 'City/Region',
        placeholder: 'Enter City/Region',
        required: true,
        width: 'half',
        validationMessages: {
          required: 'City/Region is required',
        },
      },
      {
        id: 'country',
        fieldType: 'text',
        name: 'country',
        apiKey: 'country',
        label: 'Country',
        placeholder: '',
        disabled: true,
        width: 'half',
      },
    ],
  },
  // Purchase Profile Section
  {
    id: 'interestedModelTestDrive',
    fieldType: 'dropdown',
    name: 'interestedModelTestDrive',
    apiKey: 'interestedModel',
    label: 'Interested Model/Trim',
    placeholder: 'Select a Model',
    options: interestedModelOptions,
  },
  {
    id: 'interestedVehicleCondition',
    fieldType: 'radio_group',
    name: 'interestedVehicleCondition',
    apiKey: 'interestedVehicleCondition',
    label: 'Interested Vehicle Condition',
    width: 'full',
    layout: 'horizontal',
    options: [
      { label: 'New Vehicle', value: 'new', default: true },
      { label: 'Used Vehicle', value: 'used' },
    ],
  },
  {
    id: 'currentCarMake',
    fieldType: 'dropdown',
    name: 'currentCarMake',
    apiKey: 'currentCarMake',
    label: 'Make of Current Car',
    placeholder: 'Select Make',
    width: 'full',
    options: carMakerOptions,
  },
  {
    id: 'purchaseType',
    fieldType: 'dropdown',
    name: 'purchaseType',
    apiKey: 'purchaseType',
    label: 'Purchase Type',
    placeholder: 'Select Purchase Type',
    width: 'full',
    options: purchaseTypeOptions,
  },
  {
    id: 'financeType',
    fieldType: 'dropdown',
    name: 'financeType',
    apiKey: 'financeType',
    label: 'Finance',
    placeholder: 'Select Finance Type',
    width: 'full',
    options: financeTypeOptions,
  },
  {
    id: 'purchaseTiming',
    fieldType: 'dropdown',
    name: 'purchaseTiming',
    apiKey: 'purchaseTiming',
    label: 'Timing',
    placeholder: 'Select Timing',
    required: true,
    width: 'full',
    validationMessages: {
      required: 'Timing is required',
    },
    options: purchaseTimingOptions,
  },
  {
    id: 'preferredContactMethod',
    fieldType: 'dropdown',
    name: 'preferredContactMethod',
    apiKey: 'preferredContactMethod',
    label: 'Preferred Contact Method',
    placeholder: 'Select Preferred Contact Method',
    width: 'full',
    options: preferredContactMethodOptions,
  },
  // Consent Section
  {
    id: 'emailConsent',
    fieldType: 'radio_group',
    name: 'emailConsent',
    apiKey: 'isEmailOptIn',
    label: 'Email Consent',
    width: 'full',
    layout: 'horizontal',
    required: true,
    validationMessages: {
      required: 'Email consent is required',
    },
    options: emailConsentOptions,
  },
  {
    id: 'callConsent',
    fieldType: 'radio_group',
    name: 'callConsent',
    apiKey: 'isCallOptIn',
    label: 'Call Consent',
    width: 'full',
    layout: 'horizontal',
    required: true,
    validationMessages: {
      required: 'Call consent is required',
    },
    options: phoneConsentOptions,
  },
  // Schedule Test Drive Section
  {
    id: 'testDriveSection',
    fieldType: 'group',
    width: 'full',
    title: 'Schedule Test Drive',
    children: [
      {
        id: 'isTestDrive',
        fieldType: 'checkbox_group',
        name: 'isTestDrive',
        apiKey: 'isTestDrive',
        label: 'Test Drive Now',
        width: 'half',
        layout: 'horizontal',
        defaultValue: ['yes'],
        options: [{ label: 'Yes', value: 'yes' }],
      },
      {
        id: 'appointmentLocationName',
        fieldType: 'content',
        label: 'Location',
        width: 'half',
        content: options?.location || '-',
      },
      {
        id: 'testDriveDate',
        fieldType: 'date_picker',
        name: 'testDriveDate',
        apiKey: 'testDriveDate',
        label: 'Date',
        required: true,
        width: 'half',
        size: 'medium',
        minDate: 'today',
        dependency: { name: 'isTestDrive', value: 'yes' },
        validationMessages: {
          required: 'Date is required',
        },
      },
      {
        id: 'testDriveTime',
        fieldType: 'dropdown',
        name: 'testDriveTime',
        apiKey: 'testDriveTime',
        label: 'Time',
        required: true,
        placeholder: 'Select Time',
        width: 'half',
        dependency: { name: 'isTestDrive', value: 'yes' },
        options: testDriveTimeOptions,
        validationMessages: {
          required: 'Time is required',
        },
      },
      {
        id: 'status',
        fieldType: 'dropdown',
        name: 'status',
        apiKey: 'status',
        label: 'Status',
        placeholder: 'Select Status',
        required: true,
        width: 'full',
        dependency: { name: 'isTestDrive', value: 'yes' },
        options: statusOptions,
        defaultValue: 'scheduled',
        validationMessages: {
          required: 'Status is required',
        },
      },
      {
        id: 'selectedModelTrim',
        fieldType: 'dropdown',
        name: 'selectedModelTrim',
        apiKey: 'selectedModelTrim',
        label: 'selected Model/Trim',
        placeholder: 'Select a Model',
        dependency: { name: 'isTestDrive', value: 'yes' },
        options: interestedModelOptions,
      },
    ],
  },
  {
    id: 'notes',
    fieldType: 'textarea',
    name: 'notes',
    apiKey: 'noteText',
    label: 'Notes',
    placeholder: 'Enter Notes',
    width: 'full',
  },
  {
    apiKey: 'terms',
    fieldType: 'disclaimers',
    id: 'terms',
    name: 'terms',
    required: false,
    terms: `    
    By clicking Create, I confirm that I have informed the prospect that their personal data will be processed by ${options?.organizationName || '-'}, Lucid Europe B.V., 
    and other relevant Lucid entities (such as local affiliates, importers, and dealers) 
    in accordance with <a href="https://lucidmotors.com/de-de/legal#privacy-policy" target="_blank" rel="noopener noreferrer">Lucid's Privacy Policy</a> and, where applicable, the Agent's own privacy policy.
    `,
  },
  {
    id: 'formButtons',
    fieldType: 'buttonGroup',
    align: 'spaceBetween',
    buttons: [
      {
        id: 'cancelButton',
        label: 'Cancel',
        size: 'medium',
        type: 'secondary',
        color: 'neutral',
        theme: 'dark',
        isFluid: true,
        onClick: options?.onCancel,
      },
      {
        id: 'submitButton',
        label: 'Create',
        type: 'primary',
        size: 'medium',
        color: 'neutral',
        theme: 'light',
        isFluid: true,
      },
    ],
  },
];
